import styled from "@emotion/styled";
import {
  Paper,
  Theme,
  Typography,
  useTheme,
  Skeleton,
} from "@yuno/design-system-web";
import { useContext, useState } from "react";
import { useIsTesting } from "@yuno/dashboard-container-mfe";
import { MainContext } from "../../../context/main-context";
import { DraggableList } from "./draggable-list";
import Xarrow from "react-xarrows";
import { useConditions } from "../../../hooks/use-conditions";
import { useTranslation } from "react-i18next";

const Container = styled(Paper)<{ theme?: Theme; isTesting: boolean }>`
  min-width: 448px;
  width: 100%;
  border-radius: 0px;
  border-right: 1px solid ${({ theme }) => theme.palette.grey[2]};
  height: ${({ isTesting }) => (isTesting ? "83.93vh" : "87.9vh")};
  overflow-y: hidden;
  padding-bottom: 24px;
`;

const Title = styled(Typography)<{ theme?: Theme }>`
  text-align: left;
  border-bottom: 1px solid ${({ theme }) => theme.palette.grey[2]};
  ${({ theme }) => theme.typography.l.bold};
  margin-bottom: 24px;
  padding: 24px;
`;

const Wrapper = styled.div`
  position: relative;
`;

const Section = styled.div`
  position: relative;
  padding: 24px;
  padding-top: 0;
  overflow-y: auto;
  height: 100%;
  padding-bottom: 80px;
`;

export const CheckoutList = ({ isLoading = false }) => {
  const theme: Theme = useTheme();
  const { isTesting } = useIsTesting();
  const { t } = useTranslation();

  const { lists, handleChange, handleSwitch } = useContext(MainContext);

  const card = lists.enrollment.find((el) => el.payment_method_type === "CARD");

  if (isLoading) {
    return (
      <div style={{ width: "450px" }}>
        <Container isTesting={isTesting}>
          <Title variant="h6">{t("titleCondition")}</Title>
          <Section>
            <Skeleton
              variant="rounded"
              height={50}
              width={"400px"}
              sx={{ marginBottom: "12px" }}
            />
            <Skeleton
              variant="rounded"
              height={50}
              width={"400px"}
              sx={{ marginBottom: "12px" }}
            />
            <Skeleton
              variant="rounded"
              height={50}
              width={"400px"}
              sx={{ marginBottom: "12px" }}
            />
            <Skeleton
              variant="rounded"
              height={50}
              width={"400px"}
              sx={{ marginBottom: "12px" }}
            />
            <Skeleton
              variant="rounded"
              height={50}
              width={"400px"}
              sx={{ marginBottom: "12px" }}
            />
          </Section>
        </Container>
      </div>
    );
  }

  return (
    <Wrapper>
      <Container isTesting={isTesting}>
        <Title variant="h6">{t("titleCondition")}</Title>
        <Section>
          {lists.enrollment.length > 0 && (
            <Xarrow
              _cpx1Offset={-15}
              _cpx2Offset={-15}
              strokeWidth={1}
              headColor={theme.palette.vivid.brand}
              color={theme.palette.grey[2]}
              headShape="circle"
              showTail={false}
              path="grid"
              startAnchor="left"
              endAnchor="left"
              start="elem1"
              end="elem2"
            />
          )}

          {lists.enrollment.length > 0 && (
            <DraggableList
              data={lists.enrollment}
              title={t('enrolledTitle')}
              onChange={(reordered) => handleChange("enrollment", reordered)}
              onSwitch={(ev, data) =>
                handleSwitch("enrollment", ev.target.checked, data)
              }
            />
          )}

          {(lists.paymentMethods.length > 0 || card) && (
            <DraggableList
              fixedList={[card]}
              data={lists.paymentMethods}
              title={t('nonEnrolledTitle')}
              onChange={(reordered) =>
                handleChange("paymentMethods", reordered)
              }
              onSwitch={(ev, data) =>
                handleSwitch("paymentMethods", ev.target.checked, data)
              }
            />
          )}
        </Section>
      </Container>
    </Wrapper>
  );
};
